import * as PIXI from 'pixi.js';
import { CELL_SIZE } from '../constants';
import { EntityData } from '../types';

// Texture cache for loaded assets
const textureCache = new Map<string, PIXI.Texture>();

export class Entity {
  public type: string;
  public x: number;
  public y: number;
  public targetX: number;
  public targetY: number;
  private glowPhase: number;
  private rotationSpeed: number;
  public sprite: PIXI.Container;
  public isWinPortal: boolean = false;

  constructor(data: EntityData, container: PIXI.Container) {
    this.type = data.type;
    this.x = data.x;
    this.y = data.y;
    this.targetX = data.x;
    this.targetY = data.y;
    this.glowPhase = Math.random() * Math.PI * 2;
    this.rotationSpeed = 0.02;
    this.sprite = this.createSprite();
    this.sprite.x = this.x * CELL_SIZE;
    this.sprite.y = this.y * CELL_SIZE;
    this.sprite.zIndex = 1; // Default z-index, will be updated by EntityManager
    container.addChild(this.sprite);
  }

  private static async loadTexture(type: string): Promise<PIXI.Texture> {
    const cacheKey = type.toLowerCase();
    
    if (textureCache.has(cacheKey)) {
      return textureCache.get(cacheKey)!;
    }

    try {
      const texture = await PIXI.Assets.load(`/assets/${cacheKey}.png`);
      textureCache.set(cacheKey, texture);
      return texture;
    } catch (error) {
      console.warn(`Failed to load texture for ${type}, using fallback`, error);
      // Return a fallback texture
      const graphics = new PIXI.Graphics();
      graphics.rect(0, 0, CELL_SIZE, CELL_SIZE);
      graphics.fill({ color: 0x888888 });
      const fallbackTexture = PIXI.RenderTexture.create({ width: CELL_SIZE, height: CELL_SIZE });
      return fallbackTexture;
    }
  }

  public static async preloadAllTextures(): Promise<void> {
    const entityTypes = [
      'portal', 'barrier', 'orb', 'crystal', 'rock', 'water', 'fire', 'key', 'door',
      'ice', 'lava', 'mirror', 'void', 'bolt', 'star', 'skull', 'coin', 'ghost', 'statue',
      'hunter', 'watcher', 'shadow', 'pursuer', 'sentinel'
    ];

    // Preload regular entity textures (phantom is loaded separately with dimension variants)
    await Promise.all(entityTypes.map(type => Entity.loadTexture(type.toUpperCase())));
    
    // Preload dimension-specific phantom textures
    await Promise.all([
      Entity.loadTexture('PHANTOMBLUE'),
      Entity.loadTexture('PHANTOMPINK'),
      Entity.loadTexture('PHANTOMGREEN'),
      Entity.loadTexture('PHANTOMYELLOW')
    ]);
  }

  private createSprite(): PIXI.Container {
    const container = new PIXI.Container();
    // Use phantomblue as default for PHANTOM entities
    const cacheKey = this.type === 'PHANTOM' ? 'phantomblue' : this.type.toLowerCase();
    
    // Check if texture is already loaded
    if (textureCache.has(cacheKey)) {
      const texture = textureCache.get(cacheKey)!;
      const sprite = new PIXI.Sprite(texture);
      sprite.width = CELL_SIZE;
      sprite.height = CELL_SIZE;
      container.addChild(sprite);
    } else {
      // Fallback to temporary placeholder while loading
      const graphics = new PIXI.Graphics();
      graphics.rect(0, 0, CELL_SIZE, CELL_SIZE);
      graphics.fill({ color: 0x888888, alpha: 0.5 });
      container.addChild(graphics);
      
      // Load texture asynchronously and replace
      const textureKey = this.type === 'PHANTOM' ? 'PHANTOMBLUE' : this.type;
      Entity.loadTexture(textureKey).then(texture => {
        container.removeChildren();
        const sprite = new PIXI.Sprite(texture);
        sprite.width = CELL_SIZE;
        sprite.height = CELL_SIZE;
        container.addChild(sprite);
      });
    }
    
    return container;
  }

  // Removed procedural drawing methods - now using PNG assets

  public moveTo(x: number, y: number): void {
    this.targetX = x;
    this.targetY = y;
  }

  public updatePosition(): boolean {
    const speed = 0.25;
    this.glowPhase += 0.04;
    
    // Special animation for WIN portals (doesn't move, just animates in place)
    if (this.isWinPortal && this.type === 'PORTAL') {
      this.sprite.alpha = Math.sin(this.glowPhase * 1.5) * 0.3 + 0.7;
      this.sprite.rotation += this.rotationSpeed;
      
      // Pulsing scale effect
      const scale = Math.sin(this.glowPhase * 2) * 0.1 + 1.05;
      this.sprite.scale.set(scale);
      
      // Set pivot to center for rotation and maintain position
      this.sprite.pivot.set(CELL_SIZE / 2, CELL_SIZE / 2);
      this.sprite.x = this.x * CELL_SIZE + CELL_SIZE / 2;
      this.sprite.y = this.y * CELL_SIZE + CELL_SIZE / 2;
      
      // Portal doesn't move, always done
      return true;
    }
    
    // Normal animation for all other entities
    this.sprite.alpha = Math.sin(this.glowPhase) * 0.15 + 0.9;
    this.sprite.rotation = 0;
    this.sprite.scale.set(1);
    this.sprite.pivot.set(0, 0);

    // Calculate movement
    const dx = (this.targetX * CELL_SIZE - this.sprite.x) * speed;
    const dy = (this.targetY * CELL_SIZE - this.sprite.y) * speed;

    if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
      this.x = this.targetX;
      this.y = this.targetY;
      this.sprite.x = this.x * CELL_SIZE;
      this.sprite.y = this.y * CELL_SIZE;
      return true;
    }

    this.sprite.x += dx;
    this.sprite.y += dy;
    return false;
  }

  public updateTexture(dimension: number): void {
    // Only update PHANTOM entities with dimension-specific sprites
    if (this.type !== 'PHANTOM') return;

    const dimensionSprites = ['phantomblue', 'phantomblue', 'phantompink', 'phantomgreen', 'phantomyellow'];
    const spriteName = dimensionSprites[dimension] || 'phantomblue';
    
    const texture = textureCache.get(spriteName);
    if (!texture) return;

    // Find the sprite child and update its texture
    const spriteChild = this.sprite.children[0];
    if (spriteChild && spriteChild instanceof PIXI.Sprite) {
      spriteChild.texture = texture;
    }
  }

  public destroy(): void {
    if (this.sprite && this.sprite.parent) {
      this.sprite.parent.removeChild(this.sprite);
      this.sprite.destroy();
    }
  }
}
